/** **드롭존에 떨어뜨린 파일이 「배경이 이미 지워진 사진」인가** (`docs/PRD.md` `7.13` **D-89**).
 *
 * 판정 자체는 `@makit/shared` 의 `judgePrecut`·`judgePrecutFromRgba` 가 한다. 이 파일은
 * **브라우저에서만 할 수 있는 일**(캔버스 디코딩)을 해서 그 함수에 넘긴다.
 *
 * ## ⭐ 웹은 PNG 를 풀 필요가 없다
 *
 * 캔버스가 **어떤 형식이든 `RGBA` 로 준다.** 그래서 앱과 달리 WebP 도 판정된다.
 * 공유 쪽의 `inflate` 는 여기서 안 쓴다 — **브라우저 디코더가 더 빠르고 더 넓다.**
 *
 * ⛔ **그래도 문턱과 규칙은 공유 쪽 하나다.** 여기서 비율을 다시 세지 않는다. 두 벌이 되면
 * **앱은 뜨는데 웹은 안 뜨는** 날이 온다 — `7.7.3a` 가 세 번 적어 둔 사고다.
 *
 * ## 값을 아끼는 순서
 *
 * | 단 | 하는 일 | 값 |
 * |---|---|---|
 * | 1 | 머리 4KB 만 읽어 **알파를 담을 수 있는 형식인가** | 4KB. **드롭존에 떨어지는 대부분(JPEG)이 여기서 끝난다** |
 * | 2 | 줄여서 그리고 화소를 읽는다 | 브라우저 디코딩 + 약 1ms(256²) |
 *
 * ## ⛔ 여기서 던지지 않는다
 *
 * 판정은 담기를 막는 일이 아니라 알리는 일이다(`7.6.5a`: 막는 화면이 아니라 알리는 화면).
 * 무슨 일이 나든 `UNKNOWN` 으로 떨어지고, `UNKNOWN` 은 시트를 안 띄운다.
 */

import {
  PRECUT_SAMPLE_LONG_SIDE,
  judgePrecutFromRgba,
  readAlphaCapability,
  type PrecutAssessment,
} from "@makit/shared";

/** 머리에서 읽을 양. `tRNS` 는 규격상 `IDAT` 앞에 오므로 이 안에 들어온다. */
const HEADER_BYTES = 4096;

function notPrecut(): PrecutAssessment {
  return { verdict: "NOT_PRECUT", reason: "NO_ALPHA_CHANNEL", measured: null };
}

function unknown(): PrecutAssessment {
  return { verdict: "UNKNOWN", reason: "UNREADABLE", measured: null };
}

/** 긴 변을 `PRECUT_SAMPLE_LONG_SIDE` 로 맞춘 크기. **원본보다 키우지 않는다** —
 *  키우면 없던 반투명이 생겨 비율이 흔들린다. */
function sampleSize(width: number, height: number): { w: number; h: number } {
  const long = Math.max(width, height);
  if (long <= PRECUT_SAMPLE_LONG_SIDE) return { w: width, h: height };
  const scale = PRECUT_SAMPLE_LONG_SIDE / long;
  return { w: Math.max(1, Math.round(width * scale)), h: Math.max(1, Math.round(height * scale)) };
}

/** 파일 하나의 판정. **드롭존에 떨어뜨린 순간 부른다.** */
export async function judgeFilePrecut(file: Blob): Promise<PrecutAssessment> {
  // 1단. 머리만 읽는다.
  let head: Uint8Array;
  try {
    head = new Uint8Array(await file.slice(0, HEADER_BYTES).arrayBuffer());
  } catch {
    return unknown();
  }
  const capability = readAlphaCapability(head);
  if (capability === "NONE") return notPrecut();
  if (capability === "UNREADABLE") return unknown();

  // 2단. 브라우저에게 풀게 하고 줄여서 알파만 읽는다.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return unknown();
  }
  try {
    const { w, h } = sampleSize(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    // ⛔ `alpha: false` 로 두지 마라. **그러면 읽는 알파가 전부 255 가 되어** 판정이
    //    언제나 `NOT_PRECUT` 이 된다. 이 한 줄이 이 파일의 전부다.
    const ctx = canvas.getContext("2d", { alpha: true, willReadFrequently: true });
    if (!ctx) return unknown();
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    return judgePrecutFromRgba(w, h, data);
  } catch {
    // 다른 출처의 이미지면 `getImageData` 가 막힌다(tainted canvas). 셀러가 떨어뜨린
    // 파일은 그럴 일이 없지만, 막히면 **모른다**이지 **아니다**가 아니다.
    return unknown();
  } finally {
    bitmap.close();
  }
}

/** 여러 장. **한 장이 실패해도 나머지를 버리지 않는다.** 순서를 지켜 돌려준다. */
export async function judgeFilesPrecut(files: readonly Blob[]): Promise<PrecutAssessment[]> {
  return Promise.all(
    files.map(async (file) => {
      try {
        return await judgeFilePrecut(file);
      } catch {
        return unknown();
      }
    }),
  );
}
